/*
  What waits for the SME. The server decides (2.4.3, `waits_for_sme`); this mirrors its one predicate so the
  card can tell "the element waits" from "only a line of a settled element waits".
*/

import type { ElementRow, LineRow } from "../../../api/types";

interface Waitable {
  state?: string | null;
  pending?: { status: string } | null;
  removal?: { status: string } | null;
}

export function waitsForSme(row: Waitable): boolean {
  return row.state === "accepted" || row.state === "added_by_hand" || row.pending?.status === "waiting" || row.removal?.status === "waiting";
}

/** The element itself waits (a new element, a Next version or a removal mark). */
export function elementWaits(element: ElementRow): boolean {
  return waitsForSme(element);
}

/** A line that waits on its own, because the element it hangs on is already settled (a follow-up line, or one sent back and reworked). */
export function lineWaitsAlone(line: LineRow, element: ElementRow): boolean {
  return (line.waits_for_sme || waitsForSme(line)) && !elementWaits(element);
}

/** A line the SME sent back and the engineer has not yet reworked. */
export function lineReturned(line: LineRow): boolean {
  return line.state === "sent_back" || line.pending?.status === "sent_back";
}

/** The element or one of its lines waits for the SME: a note may be added. */
export function anythingWaits(element: ElementRow, lines: readonly LineRow[]): boolean {
  return elementWaits(element) || lines.some((line) => line.waits_for_sme || waitsForSme(line));
}

/** What kind of wait an element is in, in the words of the inbox (kind_of_wait of the inbox entry). */
export function kindOfWait(element: ElementRow, lines: readonly LineRow[]): "new" | "next_version" | "light_next_version" | "removal" | "follow_up_lines" | null {
  if (element.removal?.status === "waiting") return "removal";
  if (element.pending?.status === "waiting") return element.pending.light ? "light_next_version" : "next_version";
  if (element.state === "accepted" || element.state === "added_by_hand") return "new";
  if (lines.some((line) => line.waits_for_sme || waitsForSme(line))) return "follow_up_lines";
  return null;
}
